import { cn } from "@/lib/utils";
import {
  DraftColInfluDetails,
  DraftRowHeader,
} from "../Components/LiveCampaignComponents/LiveCampaignDraftComponents";
import { Button } from "@/shadcnComponents/ui/button";
import { CalendarIcon, PlusIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcnComponents/ui/popover";
import { Calendar } from "@/shadcnComponents/ui/calendar";
import { format } from "date-fns";
import { useState } from "react";
import { PopoverClose } from "@radix-ui/react-popover";
import {
  CollabStats,
  SelectedInfluencer,
} from "../CampaignInterface/LiveCampaignInterface";

interface LiveCampaignSetCustomDatesProps {
  selectedInfluencers: SelectedInfluencer[];
  collabStats: CollabStats;
  onClickCheckLivePerformance?: () => void;
  handleSelectDate: (
    influencerId: string,
    date: Date,
    name: string
  ) => Promise<void>;
}

export const LiveCampaignSetCustomDates = ({
  selectedInfluencers,
  handleSelectDate,
  collabStats,
  onClickCheckLivePerformance,
}: LiveCampaignSetCustomDatesProps) => {
  // Remove the shared endDate state
  const [selectedDates, setSelectedDates] = useState<{ [key: string]: Date }>(
    {}
  );
  const [fade, setFade] = useState(false);

  const handleChooseDate = (value: Date, influencerId: string) => {
    setFade(true);
    setTimeout(() => {
      setSelectedDates((prev) => ({
        ...prev,
        [influencerId]: value,
      }));
      setFade(false);
    }, 300);
  };

  return (
    <div className="flex w-full flex-col items-start  py-3 md:py-6 lg:py-12 px-[24px]">
      <div className="w-full flex justify-between">
        <div className="flex flex-col  gap-2 ">
          <h1 className="text-[#121212] font-[Inter] text-[18px] not-italic font-semibold leading-[normal]">
            Customize Live Dates
          </h1>
          <span className="text-[color:var(--Natural-800,#424242)] text-xs not-italic font-normal leading-5">
            Set unique start dates for each influencer. If you don't choose a
            date, the original campaign date will apply. Each campaign lasts at
            least 3 days.
          </span>
        </div>
        {/* make it live button if any approved */}
        <Button
          variant="outline"
          className="text-[12px] flex items-center gap-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
          onClick={() => {
            onClickCheckLivePerformance();
          }}
          disabled={!collabStats?.isLiveDatePresent}
        >
          Check Live Performance
        </Button>
      </div>

      <div className="w-full flex flex-col gap-[5px] mt-[40px] ">
        <div className={cn(`grid p-2`, `grid-cols-${currentTable?.length} `)}>
          {currentTable?.map((item, index) => (
            <span
              className=" flex flex-wrap justify-start p-1 w-full text-[color:var(--Natural-500,#9E9E9E)] text-sm not-italic font-semibold leading-5 "
              key={`row-item-${index}`}
            >
              {item}
            </span>
          ))}
        </div>
        {selectedInfluencers
          ?.filter((item) => item?.brandApproval === "accepted")
          ?.map((Influencer, index) => {
            const influencerId = Influencer?.influencerDetails?._id;
            const currentDate =
              selectedDates[influencerId] || Influencer?.liveAt;

            return (
              <div
                key={index}
                className="w-full border-t-2 border-b-2 border-t-[#EEE] border-b-[#EEE] px-2.5 py-[15px] "
              >
                <DraftRowHeader
                  columnCount={currentTable?.length}
                  columnArray={[
                    //first Column
                    <DraftColInfluDetails Influencer={Influencer} />,
                    //second column
                    `${
                      Influencer?.influencerDetails?.avgEngagementPercentage?.toFixed(
                        1
                      ) ?? "NA"
                    }%`,
                    //third column
                    Math.ceil(Influencer?.influencerDetails?.avgLikes) ?? "NA",
                    //fourth column,
                    `${
                      Math.ceil(Influencer?.influencerDetails?.avgComments) ??
                      "NA"
                    } `,
                    //fifth column
                    <p className="text-[color:var(--Natural-800,#424242)]  text-sm not-italic font-semibold leading-5">
                      {Influencer?.influencerDetails?.collabCount > 0
                        ? "Yes"
                        : "No"}
                    </p>,
                    //sixth column
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          disabled={
                            Influencer?.liveAt !== undefined &&
                            Influencer?.liveAt !== null
                          }
                          variant="destructive"
                          className={`text-[12px] flex items-center gap-1  ${
                            currentDate
                              ? "bg-[#36D7CC33] text-[#115E59] "
                              : "bg-[#DC2627]"
                          } `}
                        >
                          {currentDate ? (
                            <>
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(currentDate, "PPP")}
                            </>
                          ) : (
                            <>
                              <PlusIcon size={20} />
                              Set Custom
                            </>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="left"
                        className="w-auto p-1 flex flex-col items-center justify-center "
                      >
                        <p className="text-sm text-red-600">
                          please select a date to confirm
                        </p>
                        <div className="flex flex-col gap-2">
                          <Calendar
                            className="bg-white rounded-lg border-"
                            mode="single"
                            selected={currentDate}
                            onSelect={(date) =>
                              handleChooseDate(date, influencerId)
                            }
                            initialFocus
                            disabled={(date) =>
                              date <=
                              new Date(new Date().setHours(23, 59, 59, 999))
                            }
                          />
                          {currentDate && (
                            <PopoverClose asChild>
                              <Button
                                onClick={() =>
                                  handleSelectDate(
                                    influencerId,
                                    currentDate,
                                    Influencer?.influencerDetails?.name
                                  )
                                }
                                className={`transition-opacity duration-300 ${
                                  fade ? "opacity-0" : "opacity-100"
                                }`}
                              >
                                Confirm
                              </Button>
                            </PopoverClose>
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>,
                  ]}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

const currentTable = [
  "Influencer details",
  "Avg. ER",
  "Avg. Likes",
  "Avg. Comments",
  "Past Collaborations",
  "Live Date",
];
